/**
 * Dashboard Page
 * Comprehensive analytics dashboard with real data
 */

import { useState, useEffect, useRef } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui';
import CarrierCutoffsCard from '../components/dashboard/CarrierCutoffsCard';
import PickupSlotsCard from '../components/dashboard/PickupSlotsCard';
import {
  ClipboardList,
  Route,
  Users,
  MapPin,
  DollarSign,
  Package,
  RotateCcw,
  Calendar,
  Loader2,
  RefreshCw,
  Snowflake,
  AlertTriangle
} from 'lucide-react';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import {
  getDashboardStats,
  getTaskLocations,
  getZoneDistribution,
  type DashboardStats,
  type TaskLocationsData,
  type ZoneDistributionData,
} from '../services/dashboard.api';
import { isValidCoordinate, filterValidGeoJSONFeatures } from '../utils/coordinates';
import { getBaghdadToday } from '../utils/date';
import { useCan } from '../contexts/useAuth';

mapboxgl.accessToken = import.meta.env.VITE_MAPBOX_TOKEN || '';

const BAGHDAD_CENTER = { lat: 33.284412, lng: 44.448563 };

export default function Dashboard() {
  const can = useCan();
  const [date, setDate] = useState(() => getBaghdadToday());
  const [isLoading, setIsLoading] = useState(true);
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [taskLocations, setTaskLocations] = useState<TaskLocationsData | null>(null);
  const [zoneDistribution, setZoneDistribution] = useState<ZoneDistributionData | null>(null);
  const [error, setError] = useState<string | null>(null);

  const mapContainer = useRef<HTMLDivElement>(null);
  const map = useRef<mapboxgl.Map | null>(null);
  const [mapLoaded, setMapLoaded] = useState(false);

  const fetchData = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const [statsData, locationsData, zoneData] = await Promise.all([
        getDashboardStats(date, 1),
        getTaskLocations(date),
        getZoneDistribution(date, 1),
      ]);
      setStats(statsData);
      setTaskLocations(locationsData);
      setZoneDistribution(zoneData);
    } catch (err) {
      console.error('Failed to fetch dashboard data:', err);
      setError('Failed to load dashboard data');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [date]);

  useEffect(() => {
    if (!mapContainer.current || map.current) return;

    map.current = new mapboxgl.Map({
      container: mapContainer.current,
      style: 'mapbox://styles/mapbox/light-v11',
      center: [BAGHDAD_CENTER.lng, BAGHDAD_CENTER.lat],
      zoom: 10,
    });

    map.current.on('load', () => {
      setMapLoaded(true);
    });

    return () => {
      map.current?.remove();
      map.current = null;
    };
  }, []);

  useEffect(() => {
    if (!map.current || !mapLoaded || !taskLocations) return;

    const sourceId = 'task-heatmap';
    
    if (map.current.getSource(sourceId)) {
      map.current.removeLayer('task-heat');
      map.current.removeLayer('task-points');
      map.current.removeSource(sourceId);
    }

    const allFeatures = taskLocations.all
      .filter(loc => isValidCoordinate(loc.lat, loc.lng))
      .map(loc => ({
        type: 'Feature' as const,
        properties: {
          weight: loc.weight,
          type: loc.type,
        },
        geometry: {
          type: 'Point' as const,
          coordinates: [loc.lng, loc.lat] as [number, number],
        },
      }));

    const geojsonData: GeoJSON.FeatureCollection = {
      type: 'FeatureCollection',
      features: filterValidGeoJSONFeatures(allFeatures),
    };

    map.current.addSource(sourceId, {
      type: 'geojson',
      data: geojsonData,
    });

    map.current.addLayer({
      id: 'task-heat',
      type: 'heatmap',
      source: sourceId,
      maxzoom: 15,
      paint: {
        'heatmap-weight': ['interpolate', ['linear'], ['get', 'weight'], 0, 0, 10, 1],
        'heatmap-intensity': ['interpolate', ['linear'], ['zoom'], 0, 1, 15, 3],
        'heatmap-color': [
          'interpolate',
          ['linear'],
          ['heatmap-density'],
          0, 'rgba(33,102,172,0)',
          0.2, 'rgb(103,169,207)',
          0.4, 'rgb(209,229,240)',
          0.6, 'rgb(253,219,199)',
          0.8, 'rgb(239,138,98)',
          1, 'rgb(178,24,43)',
        ],
        'heatmap-radius': ['interpolate', ['linear'], ['zoom'], 0, 2, 15, 20],
        'heatmap-opacity': ['interpolate', ['linear'], ['zoom'], 7, 1, 15, 0.6],
      },
    });

    map.current.addLayer({
      id: 'task-points',
      type: 'circle',
      source: sourceId,
      minzoom: 12,
      paint: {
        'circle-radius': ['interpolate', ['linear'], ['zoom'], 12, 4, 16, 8],
        'circle-color': [
          'match',
          ['get', 'type'],
          'pickup', '#3B82F6',
          'rto', '#F97316',
          'cash', '#D97706',
          '#6B7280',
        ],
        'circle-stroke-color': '#ffffff',
        'circle-stroke-width': 1,
        'circle-opacity': 0.8,
      },
    });

    const validLocs = taskLocations.all.filter(loc => isValidCoordinate(loc.lat, loc.lng));
    if (validLocs.length > 0) {
      const bounds = new mapboxgl.LngLatBounds();
      validLocs.forEach(loc => {
        bounds.extend([loc.lng, loc.lat]);
      });
      map.current.fitBounds(bounds, { padding: 50, maxZoom: 12 });
    }
  }, [mapLoaded, taskLocations]);


  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
    }).format(value);
  };

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Dashboard</h1>
          <p className="text-gray-500 mt-1">Fleet Management Analytics</p>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2">
            <Calendar className="h-4 w-4 text-gray-500" />
            <input
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
          <button
            onClick={fetchData}
            disabled={isLoading}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 text-sm font-medium"
          >
            {isLoading ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <RefreshCw className="h-4 w-4" />
            )}
            Refresh
          </button>
        </div>
      </div>

      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
          {error}
        </div>
      )}

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-6">
        <Card className="col-span-1">
          <div className="flex flex-col">
            <div className="flex items-center gap-2 mb-2">
              <div className="p-2 bg-blue-100 rounded-lg">
                <ClipboardList className="h-4 w-4 text-blue-600" />
              </div>
              <span className="text-xs text-gray-500 font-medium">Total Tasks</span>
            </div>
            <p className="text-2xl font-bold text-gray-900">
              {isLoading ? '...' : stats?.tasks.total || 0}
            </p>
          </div>
        </Card>

        <Card className="col-span-1">
          <div className="flex flex-col">
            <div className="flex items-center gap-2 mb-2">
              <div className="p-2 bg-indigo-100 rounded-lg">
                <Package className="h-4 w-4 text-indigo-600" />
              </div>
              <span className="text-xs text-gray-500 font-medium">Pickups</span>
            </div>
            <p className="text-2xl font-bold text-gray-900">
              {isLoading ? '...' : stats?.tasks.pickups || 0}
            </p>
          </div>
        </Card>

        <Card className="col-span-1">
          <div className="flex flex-col">
            <div className="flex items-center gap-2 mb-2">
              <div className="p-2 bg-orange-100 rounded-lg">
                <RotateCcw className="h-4 w-4 text-orange-600" />
              </div>
              <span className="text-xs text-gray-500 font-medium">RTOs</span>
            </div>
            <p className="text-2xl font-bold text-gray-900">
              {isLoading ? '...' : stats?.tasks.rtos || 0}
            </p>
          </div>
        </Card>

        <Card className="col-span-1">
          <div className="flex flex-col">
            <div className="flex items-center gap-2 mb-2">
              <div className="p-2 bg-green-100 rounded-lg">
                <Route className="h-4 w-4 text-green-600" />
              </div>
              <span className="text-xs text-gray-500 font-medium">Routes</span>
            </div>
            <p className="text-2xl font-bold text-gray-900">
              {isLoading ? '...' : stats?.routes.total || 0}
            </p>
          </div>
        </Card>

        <Card className="col-span-1">
          <div className="flex flex-col">
            <div className="flex items-center gap-2 mb-2">
              <div className="p-2 bg-purple-100 rounded-lg">
                <Users className="h-4 w-4 text-purple-600" />
              </div>
              <span className="text-xs text-gray-500 font-medium">Drivers</span>
            </div>
            <p className="text-2xl font-bold text-gray-900">
              {isLoading ? '...' : stats?.drivers.required || 0}
            </p>
          </div>
        </Card>

        <Card className="col-span-1">
          <div className="flex flex-col">
            <div className="flex items-center gap-2 mb-2">
              <div className="p-2 bg-emerald-100 rounded-lg">
                <DollarSign className="h-4 w-4 text-emerald-600" />
              </div>
              <span className="text-xs text-gray-500 font-medium">Cost/Parcel</span>
            </div>
            <p className="text-xl font-bold text-gray-900">
              {isLoading ? '...' : formatCurrency(stats?.costs.costPerParcel || 0)}
            </p>
          </div>
        </Card>

        <Card className="col-span-1">
          <div className="flex flex-col">
            <div className="flex items-center gap-2 mb-2">
              <div className="p-2 bg-cyan-100 rounded-lg">
                <Package className="h-4 w-4 text-cyan-600" />
              </div>
              <span className="text-xs text-gray-500 font-medium">Total Orders</span>
            </div>
            <p className="text-2xl font-bold text-gray-900">
              {isLoading ? '...' : stats?.orders.total || 0}
            </p>
          </div>
        </Card>
      </div>

      {/*
        Cold chain. Shown only on days that actually have cold work, so it never
        becomes a row of zeros people learn to skip.

        Two numbers, and the second is the one that matters: a task is atomic to
        the router, so flagging one order cold sends EVERY order on that task to
        a refrigerated van. 274 cold orders pulled 719 along across 16 days of
        production. Orders pinned, not cold orders, is what a two-van fleet has
        to carry — and peak-day pinned above ~250 is what triggers the rewrite.
      */}
      {!!stats?.coldChain && stats.coldChain.routes > 0 && (
        <div className="mb-6">
          <Card>
            <div className="flex flex-wrap items-center gap-x-8 gap-y-4">
              <div className="flex items-center gap-2">
                <div className="p-2 bg-sky-100 rounded-lg">
                  <Snowflake className="h-4 w-4 text-sky-600" />
                </div>
                <span className="text-sm font-medium text-gray-900">Cold chain</span>
              </div>

              <div>
                <p className="text-xs text-gray-500 font-medium">Cold routes</p>
                <p className="text-xl font-bold text-gray-900">{stats.coldChain.routes}</p>
              </div>

              <div>
                <p className="text-xs text-gray-500 font-medium">Cold orders</p>
                <p className="text-xl font-bold text-gray-900">{stats.coldChain.coldOrders}</p>
              </div>

              <div>
                <p
                  className="text-xs text-gray-500 font-medium"
                  title="Every order on a cold task, not just the cold ones — a task cannot be split, so all of them ride the refrigerated van."
                >
                  Orders pinned
                </p>
                <p className="text-xl font-bold text-gray-900">
                  {stats.coldChain.ordersPinned}
                  {stats.coldChain.coldOrders > 0 && (
                    <span className="ml-1 text-sm font-normal text-gray-500">
                      ×{(stats.coldChain.ordersPinned / stats.coldChain.coldOrders).toFixed(1)}
                    </span>
                  )}
                </p>
              </div>

              <div>
                <p className="text-xs text-gray-500 font-medium">Refrigerated vans</p>
                <p className="text-xl font-bold text-gray-900">
                  {stats.coldChain.capableDrivers}
                </p>
              </div>

              {stats.coldChain.capableDrivers === 0 && (
                <div className="flex items-start gap-2 text-amber-700 bg-amber-50 rounded-lg px-3 py-2">
                  <AlertTriangle className="h-4 w-4 flex-shrink-0 mt-0.5" />
                  <p className="text-xs">
                    No refrigerated van is marked in Schedules, so cold routes will accept any
                    driver. Mark the cold vans to turn the guard on.
                  </p>
                </div>
              )}
            </div>
          </Card>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        <Card padding="none" className="overflow-hidden">
          <CardHeader className="px-4 pt-4 pb-2 border-b border-gray-100">
            <CardTitle className="flex items-center gap-2">
              <MapPin className="h-5 w-5 text-gray-500" />
              Zone Distribution
            </CardTitle>
          </CardHeader>
          <CardContent className="p-4">
            {isLoading ? (
              <div className="h-[350px] flex items-center justify-center">
                <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
              </div>
            ) : zoneDistribution ? (
              <div className="h-[350px] overflow-y-auto">
                <div className="space-y-2">
                  {zoneDistribution.zones.map((zone) => {
                    const maxTotal = Math.max(
                      ...zoneDistribution.zones.map(z => z.total),
                      zoneDistribution.unzoned.total,
                      1
                    );
                    const widthPercent = (zone.total / maxTotal) * 100;
                    return (
                      <div key={zone.zoneId} className="flex items-center gap-3">
                        <div
                          className="w-3 h-3 rounded-full flex-shrink-0"
                          style={{ backgroundColor: zone.color }}
                        />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between mb-1">
                            <span className="text-sm font-medium text-gray-700 truncate">
                              {zone.name}
                            </span>
                            <span className="text-sm text-gray-500 ml-2">
                              {zone.total} ({zone.pickups}P / {zone.rtos}R)
                            </span>
                          </div>
                          <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                            <div
                              className="h-full rounded-full transition-all"
                              style={{
                                width: `${widthPercent}%`,
                                backgroundColor: zone.color,
                              }}
                            />
                          </div>
                        </div>
                      </div>
                    );
                  })}
                  {zoneDistribution.unzoned.total > 0 && (
                    <div className="flex items-center gap-3">
                      <div className="w-3 h-3 rounded-full flex-shrink-0 bg-gray-400" />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-sm font-medium text-gray-700">
                            Unzoned
                          </span>
                          <span className="text-sm text-gray-500">
                            {zoneDistribution.unzoned.total} ({zoneDistribution.unzoned.pickups}P / {zoneDistribution.unzoned.rtos}R)
                          </span>
                        </div>
                        <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                          <div
                            className="h-full bg-gray-400 rounded-full"
                            style={{
                              width: `${(zoneDistribution.unzoned.total / Math.max(...zoneDistribution.zones.map(z => z.total), zoneDistribution.unzoned.total, 1)) * 100}%`,
                            }}
                          />
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div className="h-[350px] flex items-center justify-center text-gray-500">
                No zone data available
              </div>
            )}
          </CardContent>
        </Card>

        <Card padding="none" className="overflow-hidden">
        <CardHeader className="px-4 pt-4 pb-2 border-b border-gray-100">
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <MapPin className="h-5 w-5 text-gray-500" />
              Task Distribution Heatmap - Baghdad
            </CardTitle>
            <div className="flex items-center gap-4 text-xs">
              <div className="flex items-center gap-1">
                <div className="w-3 h-3 bg-blue-500 rounded-full" />
                <span className="text-gray-600">Pickups ({taskLocations?.summary.pickupLocations || 0})</span>
              </div>
              <div className="flex items-center gap-1">
                <div className="w-3 h-3 bg-orange-500 rounded-full" />
                <span className="text-gray-600">RTOs ({taskLocations?.summary.rtoLocations || 0})</span>
              </div>
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          <div ref={mapContainer} className="h-[350px] w-full" />
        </CardContent>
      </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mt-6">
        <Card padding="none">
          <CardHeader className="px-4 pt-4 pb-2 border-b border-gray-100">
            <CardTitle className="text-sm">Route Status</CardTitle>
          </CardHeader>
          <CardContent className="p-4">
            {isLoading ? (
              <div className="flex items-center justify-center py-4">
                <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
              </div>
            ) : stats ? (
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">In Progress</span>
                  <span className="text-sm font-semibold text-green-600">{stats.routes.inProgress}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">Completed</span>
                  <span className="text-sm font-semibold text-gray-600">{stats.routes.completed}</span>
                </div>
              </div>
            ) : null}
          </CardContent>
        </Card>

        <Card padding="none">
          <CardHeader className="px-4 pt-4 pb-2 border-b border-gray-100">
            <CardTitle className="text-sm">Cost Summary</CardTitle>
          </CardHeader>
          <CardContent className="p-4">
            {isLoading ? (
              <div className="flex items-center justify-center py-4">
                <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
              </div>
            ) : stats ? (
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">Total Cost</span>
                  <span className="text-sm font-semibold text-gray-900">
                    {formatCurrency(stats.costs.totalCost)}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">Cost per Order</span>
                  <span className="text-sm font-semibold text-gray-900">
                    {formatCurrency(stats.costs.avgCostPerOrder)}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">Cost per Parcel</span>
                  <span className="text-sm font-semibold text-emerald-600">
                    {formatCurrency(stats.costs.costPerParcel)}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">Avg Capacity/Driver</span>
                  <span className="text-sm font-semibold text-gray-900">
                    {stats.drivers.avgCapacityPerDriver.toFixed(1)} orders
                  </span>
                </div>
              </div>
            ) : null}
          </CardContent>
        </Card>

        <Card padding="none">
          <CardHeader className="px-4 pt-4 pb-2 border-b border-gray-100">
            <CardTitle className="text-sm">Task Summary</CardTitle>
          </CardHeader>
          <CardContent className="p-4">
            {isLoading ? (
              <div className="flex items-center justify-center py-4">
                <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
              </div>
            ) : stats ? (
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">Total Tasks</span>
                  <span className="text-sm font-semibold text-gray-900">{stats.tasks.total}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">Pickup Tasks</span>
                  <span className="text-sm font-semibold text-blue-600">{stats.tasks.pickups}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">RTO Tasks</span>
                  <span className="text-sm font-semibold text-orange-600">{stats.tasks.rtos}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">Pickup Ratio</span>
                  <span className="text-sm font-semibold text-gray-900">
                    {stats.tasks.total > 0
                      ? `${Math.round((stats.tasks.pickups / stats.tasks.total) * 100)}%`
                      : '0%'}
                  </span>
                </div>
              </div>
            ) : null}
          </CardContent>
        </Card>
      </div>

      {/* Carrier cutoffs */}
      {(can('cutoffs.view') || can('slots.view')) && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mt-6">
          {can('cutoffs.view') && <CarrierCutoffsCard />}
          {can('slots.view') && <PickupSlotsCard />}
        </div>
      )}
    </div>
  );
}
